/**
 * Tela /stores (TH-53160 / M1.E4.S3.2).
 *
 * - super_admin / tenant_admin: lista todas + cria + edita + soft-delete
 * - store_manager: read-only, vê só sua loja (backend já filtra via
 *   _check_can_access; useQuery dispara mesmo, backend devolve 1 linha).
 * - operator e demais: 403 inline.
 *
 * Refatorado em TH-54207 para usar <DataTable> com header mono + status pill
 * (padrão painel-screens.jsx:179-196). Loading/erro continuam tratados via
 * banner inline acima da tabela; quando há dados, usa rows do DataTable.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import {
  StoreFormDialog,
  type Store,
} from "@/pages/stores/StoreFormDialog";
import { StoreMap } from "@/pages/stores/StoreMap";

interface PagedResponse {
  data: Store[];
  meta: { page: number; per_page: number; total: number };
}

const PER_PAGE = 20;
// Modo Mapa carrega de uma vez o máximo permitido pela API. Tenants com
// mais de 500 lojas ficam de fora do v1 — follow-up: paginar bounds (TH-XXXXX).
const MAP_FETCH_LIMIT = 500;

async function fetchStores(page: number): Promise<PagedResponse> {
  const res = await fetch(`/api/stores?page=${page}&per_page=${PER_PAGE}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as PagedResponse;
}

/**
 * Busca todas as lojas (até MAP_FETCH_LIMIT) para alimentar o mapa.
 * Faz múltiplas chamadas paginadas porque a API limita per_page a 100.
 */
async function fetchAllStoresForMap(): Promise<Store[]> {
  const pageSize = 100;
  const all: Store[] = [];
  let page = 1;
  while (all.length < MAP_FETCH_LIMIT) {
    const res = await fetch(`/api/stores?page=${page}&per_page=${pageSize}`, {
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const body = (await res.json()) as PagedResponse;
    all.push(...body.data);
    if (body.data.length < pageSize) break;
    if (all.length >= body.meta.total) break;
    page += 1;
  }
  return all;
}

async function deactivateStore(id: string): Promise<void> {
  const res = await fetch(`/api/stores/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`HTTP ${res.status}`);
  }
}

async function reactivateStore(id: string): Promise<void> {
  const res = await fetch(`/api/stores/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ active: true }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

/**
 * Pill inline de status ativo/inativo — wrapper fino sobre <StatusPill>
 * para preservar a semântica "Ativa/Inativa" como label fixo.
 */
function ActivePill({ active }: { active: boolean }) {
  return (
    <StatusPill tone={active ? "success" : "muted"}>
      {active ? "Ativa" : "Inativa"}
    </StatusPill>
  );
}

type ViewMode = "list" | "map";

export function StoresPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [view, setView] = useState<ViewMode>("list");
  const [editing, setEditing] = useState<Store | null>(null);
  const [creating, setCreating] = useState(false);

  const canManage =
    user?.role === "super_admin" || user?.role === "tenant_admin";
  const canRead = canManage || user?.role === "store_manager";

  const query = useQuery({
    queryKey: ["stores", page],
    queryFn: () => fetchStores(page),
    staleTime: 30_000,
    enabled: canRead && view === "list",
  });

  const mapQuery = useQuery({
    queryKey: ["stores", "map"],
    queryFn: () => fetchAllStoresForMap(),
    staleTime: 60_000,
    enabled: canRead && view === "map",
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? reactivateStore(id) : deactivateStore(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["stores"] }),
  });

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="OPERAÇÃO / LOJAS" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin
              ou store_manager.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const viewToggle = (
    <div
      role="tablist"
      aria-label="Modo de visualização"
      className="inline-flex overflow-hidden rounded-md border border-[var(--color-border)]"
    >
      <button
        type="button"
        role="tab"
        aria-selected={view === "list"}
        className={
          "px-3 py-1.5 text-xs font-medium uppercase tracking-wide " +
          (view === "list"
            ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
            : "bg-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]")
        }
        onClick={() => setView("list")}
      >
        Lista
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={view === "map"}
        className={
          "px-3 py-1.5 text-xs font-medium uppercase tracking-wide " +
          (view === "map"
            ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
            : "bg-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]")
        }
        onClick={() => setView("map")}
      >
        Mapa
      </button>
    </div>
  );

  const headerActions = (
    <div className="flex items-center gap-3">
      {viewToggle}
      {canManage ? (
        <Button onClick={() => setCreating(true)}>Nova loja</Button>
      ) : null}
    </div>
  );

  const columns: DataTableColumn<Store>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (s) => (
        <Link
          to="/stores/$storeId"
          params={{ storeId: s.id }}
          className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
        >
          {s.name}
        </Link>
      ),
    },
    {
      key: "code",
      header: "Código",
      mono: true,
      cell: (s) => (
        <span className="text-[var(--color-muted-foreground)]">
          {s.code ?? "—"}
        </span>
      ),
    },
    {
      key: "city",
      header: "Cidade",
      cell: (s) => (
        <span className="text-[var(--color-muted-foreground)]">
          {s.city ?? "—"}
        </span>
      ),
    },
    {
      key: "state",
      header: "UF",
      cell: (s) => (
        <span className="text-[var(--color-muted-foreground)]">
          {s.state ?? "—"}
        </span>
      ),
    },
    {
      key: "tv_count",
      header: "TVs",
      align: "right",
      mono: true,
      cell: (s) => s.tv_count,
    },
    {
      key: "active",
      header: "Status",
      cell: (s) => <ActivePill active={s.active} />,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            cell: (s: Store) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(s)}
                >
                  Editar
                </Button>
                {s.active ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--color-destructive)]"
                    disabled={toggleActive.isPending}
                    onClick={() =>
                      toggleActive.mutate({ id: s.id, active: false })
                    }
                  >
                    Desativar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={toggleActive.isPending}
                    onClick={() =>
                      toggleActive.mutate({ id: s.id, active: true })
                    }
                  >
                    Reativar
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const rows = query.data?.data ?? [];
  const subtitle = query.data
    ? `${query.data.meta.total} ${query.data.meta.total === 1 ? "loja" : "lojas"}`
    : undefined;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / LOJAS"
        title="Lojas"
        actions={headerActions}
      >
        <div className="mx-auto max-w-6xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {canManage
              ? "Gerenciar lojas do tenant."
              : "Loja sob sua gestão (somente leitura)."}
          </p>

          {view === "list" ? (
            <>
              {query.error ? (
                <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
                  erro ao carregar
                </div>
              ) : null}

              <div className="mt-6">
                <DataTable<Store>
                  columns={columns}
                  rows={rows}
                  title="Lojas"
                  subtitle={subtitle}
                  rowKey={(s) => s.id}
                  emptyMessage={
                    query.isLoading ? "carregando…" : "nenhuma loja encontrada"
                  }
                />
              </div>

              {query.data ? (
                <div className="mt-4">
                  <Pagination
                    page={page}
                    perPage={PER_PAGE}
                    total={query.data.meta.total}
                    onPageChange={setPage}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-6">
              {mapQuery.error ? (
                <div className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
                  erro ao carregar lojas para o mapa
                </div>
              ) : mapQuery.isLoading || !mapQuery.data ? (
                <div className="flex h-[480px] w-full items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 text-xs text-[var(--color-muted-foreground)]">
                  carregando…
                </div>
              ) : (
                <StoreMap stores={mapQuery.data} />
              )}
            </div>
          )}
        </div>

        {canManage ? (
          <>
            <StoreFormDialog
              open={creating}
              onOpenChange={setCreating}
            />
            {editing ? (
              <StoreFormDialog
                open
                onOpenChange={(o) => {
                  if (!o) setEditing(null);
                }}
                store={editing}
              />
            ) : null}
          </>
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}
